/**
 * THE CONCEPT'S DATA ADAPTER — the only place the generated-concept level
 * talks to the network.
 *
 * `concept.js` is pure so the verify suite can drive it; this file is the thin
 * boundary that goes and gets the records for it. Keeping them apart is what
 * lets every composition rule be asserted under Node with no fetch stub.
 *
 * HARD RULE #2: the client never calls HubSpot. Both reads go through
 * `lib/api.js` to the JWT backend, which brokers HubSpot.
 */

import { api } from '../../../lib/api.js'
import {createSessionCache} from '../../../lib/sessionGeneration.js'
import { normalizeSchool } from '../../../lib/schools.js'
import { normalizeChampionships } from '../../../lib/championships.js'
import { toUspsCode } from '../../../lib/usStates.js'
import { tokensOf, rankChampionshipMatches } from './concept.js'

const s = (v) => (v == null ? '' : String(v).trim())

/* ------------------------------------------------------------------ *
 * The championship universe
 * ------------------------------------------------------------------ */

/**
 * ⚠️ THE ROUTE THE CONCEPT USED TO CALL DOES NOT EXIST. Measured on the live
 * site, 2026-09-09, by instrumenting `window.fetch` and pressing the button:
 *
 *   GET /api/championships/search?q=…  →  404  "Cannot GET /api/championships/search"
 *
 * `sr-portal-backend/server.js` serves `/api/championships`,
 * `/api/championships/all`, `/schema`, `/feed` and `/surface` — there is no
 * search route and never was. `api.championshipSearch` has been pointed at a
 * 404 the whole time.
 *
 * That alone would have been a visible failure. What made it invisible is what
 * `engine/pickerData.js#searchChampionships` did NEXT: it swallowed the error
 * and fell through to **six hard-coded DEMO records** — two of which are
 * fictional HIGH SCHOOLS ("SC 4A Football State Championship", "Coastal Empire
 * 5A Basketball Final"). So a brief could quietly seed a real, recordable build
 * from invented data, with a receipt claiming "championship record".
 *
 * That is Brian's *"generated concept should not reference a high school"* at
 * its root, and it is worse than a wrong school: it is a fabricated one.
 *
 * So this module talks to `/api/championships/all` — the server-built snapshot,
 * **the whole universe in ONE request** (14,797 records, measured live) — and
 * matches client-side. There is no demo path here at all: a concept that cannot
 * reach the real universe reports that it could not, and builds nothing.
 */

const universeCache = createSessionCache()

/** Clear the cached universe — exported so a retry genuinely re-fetches. */
export function forgetUniverse() { universeCache.clear() }

/**
 * Every championship, normalized. `202` is the snapshot still building — a real
 * state with its own message, never an error and never a reason to fall back to
 * paging (that is what made the board take four minutes).
 */
export async function loadUniverse(signal) {
  return universeCache.load('championships', async () => {
  const res = await api.championshipsAll({ signal })
  if (res && res.building) {
    const e = new Error('The championship snapshot is still building on the server. Give it a moment and press Source the build again.')
    e.code = 'building'
    throw e
  }
  const records = normalizeChampionships(res)
  if (!records.length) {
    const e = new Error('The championship universe came back empty. Nothing was matched, and nothing was invented.')
    e.code = 'empty_universe'
    throw e
  }
  return records
  })
}

/**
 * Resolve the brief against the real universe.
 *
 * ⚠️ RETIRED RECORDS ARE EXCLUDED. The object carries rows literally prefixed
 * `RETIRED - ` (e.g. "RETIRED - Atlantic Coast Conference Men's Water Polo
 * Championship"). Offering one as a candidate would invite a build against a
 * championship that no longer happens.
 */
export async function findChampionships(brief, signal) {
  const all = await loadUniverse(signal)
  const live = all.filter((r) => !/^\s*RETIRED\b/i.test(s(r.name)))
  return rankChampionshipMatches(brief, live)
}

/**
 * Find the SCHOOL behind a championship record — the record that carries the
 * OWNED crest and the verified brand colours, i.e. the two things the operator
 * is deliberately not being asked for.
 *
 * ⚠️ IT RETURNS null RATHER THAN A NEAR MISS. The crest goes on the ring. A
 * school picked because its name shares a word with the champion's is how a
 * build ends up carrying somebody else's mark, and unlike a wrong title nobody
 * downstream can see it is wrong — it is a real crest, faithfully rendered, for
 * the wrong customer. So the name must match on WHOLE TOKENS, and every token
 * of the shorter name must be present in the longer one.
 *
 * A miss is not a failure: `conceptBlockers()` reports "no owned crest on file"
 * and the build stops with a reason, which is the honest state.
 */
export async function searchSchools(query, signal) {
  const q = s(query)
  if (!q) return []
  const res = await api.schools(q, { signal })
  if (res?.searchError) throw new Error('School search unavailable. Try again before choosing a school.')
  const list = res?.results || res?.data || res || []
  if (!Array.isArray(list)) throw new Error('School search returned an unreadable response. Try again.')
  return list.map(normalizeSchool).filter(Boolean)
}

/** School identity is decided before asset availability. Search results can
 * contain a university whose CITY is the champion's name. Neither an owned
 * URL nor a high search rank is evidence that it is the right customer. */
const SCHOOL_WORDS = new Set(['the', 'of', 'at', 'high', 'middle', 'school', 'schools', 'university', 'college'])

function schoolKind(text) {
  if (/\bhigh school\b|\bhs state\b/i.test(text)) return 'high'
  if (/\bmiddle school\b/i.test(text)) return 'middle'
  if (/\buniversity\b|\bcollege\b/i.test(text)) return 'college'
  return ''
}

function identityTokens(row) {
  let words = tokensOf(row.name)
  // Only strip LOCATION SUFFIXES. Removing all city tokens would erase the
  // customer's own name in "Tahlequah High School, Tahlequah, Oklahoma".
  for (const suffix of [row.state, row.city]) {
    const tail = tokensOf(suffix)
    if (tail.length && words.length > tail.length &&
        tail.every((t, i) => words[words.length - tail.length + i] === t)) {
      words = words.slice(0, -tail.length)
    }
  }
  return words.filter(t => !SCHOOL_WORDS.has(t))
}

export async function resolveSchool({ brief = null, championship = null, signal } = {}) {
  const ch = championship || {}
  const fromRecord = s(ch.school) || s(ch.champion)
  const briefText = s(brief && brief.raw)
  const queries = [...new Set([fromRecord, briefText].filter(Boolean))]
  const pool = new Map()
  for (const q of queries) {
    for (const row of await searchSchools(q, signal)) pool.set(row.id, row)
  }
  const candidates = [...pool.values()]
  if (!candidates.length) {
    return { school: null, candidates: [], reason: 'no_match', queried: queries[0] || '' }
  }

  // A named champion is stronger than incidental words in a long brief.
  const want = new Set(tokensOf(fromRecord || briefText))
  const expectedKind = schoolKind(briefText) || schoolKind([ch.segment, ch.division].filter(Boolean).join(' '))
  const expectedState = toUspsCode(ch.state)
  const matches = candidates.filter(row => {
    const raw = row.raw || {}
    const kind = schoolKind([raw.segment, raw.orgType, row.name].filter(Boolean).join(' '))
    if (expectedKind && kind !== expectedKind) return false
    // Missing geography is unresolved too, when the championship supplies it.
    if (expectedState && toUspsCode(row.state) !== expectedState) return false
    const identity = identityTokens(row)
    return identity.length > 0 && identity.some(t => t !== 'state') && identity.every(t => want.has(t))
  })
  const common = { candidates: candidates.slice(0, 8), queried: queries[0] || '' }
  if (!matches.length) return { ...common, school: null, reason: 'identity_mismatch' }
  if (matches.length > 1) return { ...common, school: null, candidates: matches.slice(0, 8), reason: 'ambiguous_school' }
  const best = matches[0]
  return { ...common, school: best.logoUrl ? best : null, nearest: best,
    reason: best.logoUrl ? 'ok' : 'no_owned_crest' }
}
